import { supabase } from '../../supabase';

// PERF-001 — read/write a finalized document PDF in the private `documents`
// bucket. The bucket RLS scopes the authenticated client to its own tenant
// folder, so a direct .download()/.upload() needs no signed-URL round-trip.
// Both functions are FAIL-SOFT: a miss/error returns null (read) or silently
// no-ops (write) so the caller always falls back to an on-demand render — a
// Storage problem never blocks a preview.

const UPLOAD_OPTS = { contentType: 'application/pdf', upsert: true };

// Chunked to avoid a call-stack overflow on large buffers (a PDF can be 100s of KB).
function uint8ToBase64(buf) {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) {
    bin += String.fromCharCode.apply(null, Array.from(buf.subarray(i, i + chunk)));
  }
  return btoa(bin);
}

function base64ToUint8(base64) {
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

/**
 * Download a stored PDF as raw bytes (for zip/download paths — skips the
 * base64 round-trip). Same fail-soft contract: null on any miss/error.
 * @returns {Promise<Uint8Array|null>}
 */
export async function fetchStoredDocumentBytes(path) {
  if (!path) return null;
  try {
    const { data, error } = await supabase.storage.from('documents').download(path);
    if (error || !data) return null;
    return new Uint8Array(await data.arrayBuffer());
  } catch {
    return null;
  }
}

/**
 * Download a stored PDF as a base64 string (the format the preview/cache speak).
 * @returns {Promise<string|null>} base64, or null on any miss/error.
 */
export async function fetchStoredDocumentBase64(path) {
  const buf = await fetchStoredDocumentBytes(path);
  return buf ? uint8ToBase64(buf) : null;
}

/**
 * Lazy-backfill a rendered PDF to Storage (RLS-obeyed) so the next open is a
 * static GET. Fail-soft — never throws; a failed backfill just re-renders next time.
 */
export async function storeDocumentBase64(path, base64) {
  if (!path || !base64) return;
  try {
    await supabase.storage.from('documents').upload(path, base64ToUint8(base64), UPLOAD_OPTS);
  } catch {
    /* non-fatal — the in-memory cache + a future send/backfill cover it */
  }
}

// PERF-001 — type → bucket folder mapping (mirrors documentStoragePath.js DIR constant).
const REMOVE_DIR = { invoice: 'invoices', estimate: 'estimates' };

/**
 * Remove ALL stored PDFs for a record from the private `documents` bucket.
 * Uses the authenticated session (RLS — the caller must own the tenant folder).
 * FAIL-SOFT: any Storage error returns { error } but NEVER throws.
 *
 * @param {{ tenantId: string, type: 'invoice'|'estimate', id: string }} opts
 * @returns {Promise<{ error: Error|null }>}
 */
export async function removeStoredDocument({ tenantId, type, id } = {}) {
  const dir = REMOVE_DIR[type];
  if (!tenantId || !dir || !id) return { error: null };
  try {
    const prefix = `${tenantId}/${dir}/${id}`;
    const { data, error: listErr } = await supabase.storage
      .from('documents')
      .list(prefix, { limit: 1000 });
    if (listErr) return { error: listErr };
    if (!data || data.length === 0) return { error: null };
    // Skip subfolder entries (id === null); the files at this depth are flat.
    const paths = data
      .filter((entry) => entry.id !== null)
      .map((entry) => `${prefix}/${entry.name}`);
    if (paths.length === 0) return { error: null };
    const { error: removeErr } = await supabase.storage.from('documents').remove(paths);
    return { error: removeErr ?? null };
  } catch (err) {
    return { error: err instanceof Error ? err : new Error(String(err)) };
  }
}
